import { zonedDate } from '../../../../utils/date-time';
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import AvatarUser from '../../../../components/AvatarUser';
import Dropdown from '../../../../components/Dropdown';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { useAppContext } from '../../../../contexts/AppContext';
import { normalizeSearch } from '../../../recorrencias/helpers';
import { FilaEsperaService } from '../../Service';
import {
    WaitlistStatus,
    WaitlistStatusLabels,
    WaitlistStatusTones,
    getWaitlistServiceLabel,
    minutesToTime,
} from '../../Enum';
import { useFilaEsperaContext } from '../Context';
import AssignModal from './AssignModal';
import { CircleCheck, Clock, Ellipsis, MessageCircle, Search, TriangleAlert } from 'lucide-react';

const service = new FilaEsperaService();

type Aba = 'aguardando' | 'encerradas';
type PendingAction = { entry: any; status: WaitlistStatus.CANCELLED | WaitlistStatus.EXPIRED };

const isOpen = (entry: any) => [WaitlistStatus.WAITING, WaitlistStatus.NOTIFIED].includes(entry?.status);

const formatDates = (entry: any) => {
    const start = entry?.preferredStartAt ? zonedDate(entry.preferredStartAt, entry.timezone).format('DD/MM/YYYY') : '';
    const end = entry?.preferredEndAt ? zonedDate(entry.preferredEndAt, entry.timezone).format('DD/MM/YYYY') : '';

    if (!start) return 'Data não informada';
    return start === end || !end ? start : `${start} a ${end}`;
};

export default function FilaEsperaList() {
    const { data, loading, error, reloadData } = useFilaEsperaContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [assignEntry, setAssignEntry] = useState<any | null>(null);
    const [pending, setPending] = useState<PendingAction | null>(null);
    const [updating, setUpdating] = useState(false);
    const [aba, setAba] = useState<Aba>('aguardando');
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => {
        const term = normalizeSearch(search);
        if (!term) return data;

        return data.filter((entry: any) =>
            normalizeSearch(`${entry?.client?.name || ''} ${getWaitlistServiceLabel(entry)} ${entry?.attendant?.name || ''}`).includes(term),
        );
    }, [data, search]);

    const openCount = filtered.filter(isOpen).length;
    const lista = useMemo(() => {
        const time = (entry: any) => new Date(entry?.createdAt || 0).getTime();

        // Aguardando segue a ordem de chegada; encerradas, da mais recente para a mais antiga.
        return aba === 'aguardando'
            ? filtered.filter(isOpen).sort((a: any, b: any) => time(a) - time(b))
            : filtered.filter((entry: any) => !isOpen(entry)).sort((a: any, b: any) => time(b) - time(a));
    }, [filtered, aba]);

    const handleUpdateStatus = async () => {
        if (!pending) return;

        try {
            setUpdating(true);
            await service.updateStatus(pending.entry._id, pending.status);
            await reloadData();
            successSnackbar(pending.status === WaitlistStatus.CANCELLED ? 'Entrada cancelada.' : 'Entrada expirada.');
            setPending(null);
        } catch (e: any) {
            console.error('Erro ao atualizar status:', e);
            errorSnackbar('Não foi possível atualizar a entrada.');
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-2 px-2" aria-busy="true">
                <div className="sf-skeleton h-10" />
                {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="sf-skeleton h-20" />
                ))}
            </div>
        );
    }

    if (error && !data.length) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar a fila</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void reloadData()}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!data.length) {
        return (
            <div className="sf-state">
                <Clock aria-hidden />
                <h2>Fila vazia</h2>
                <p>Clientes entram na fila ao agendar um dia sem horários. Quando uma vaga abre, eles recebem a oferta.</p>
            </div>
        );
    }

    return (
        <div className="sf-page px-2">
            <div className="sf-toolbar-row">
                <div className="sf-segmented" role="group" aria-label="Situação da fila">
                    <button type="button" aria-pressed={aba === 'aguardando'} onClick={() => setAba('aguardando')}>
                        Aguardando
                        <span className="sf-segmented-count">{openCount}</span>
                    </button>
                    <button type="button" aria-pressed={aba === 'encerradas'} onClick={() => setAba('encerradas')}>
                        Encerradas
                        <span className="sf-segmented-count">{filtered.length - openCount}</span>
                    </button>
                </div>
                <label className="sf-search">
                    <Search aria-hidden />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar cliente ou serviço"
                        aria-label="Buscar cliente, serviço ou atendente"
                    />
                </label>
            </div>

            {lista.length === 0 ? (
                <div className="sf-state">
                    {search ? <Search aria-hidden /> : <CircleCheck aria-hidden />}
                    <h2>
                        {search
                            ? 'Nenhum resultado nesta aba'
                            : aba === 'aguardando'
                                ? 'Ninguém aguardando agora'
                                : 'Nenhuma entrada encerrada'}
                    </h2>
                </div>
            ) : (
                <ul className="sf-group [--sf-row-inset:4rem]">
                    {lista.map((entry: any) => {
                        const clientName = entry?.client?.name || 'Cliente';
                        const phone = String(entry?.client?.phoneNumber || '').replace(/\D/g, '');
                        const open = isOpen(entry);
                        const status = entry.status as WaitlistStatus;

                        return (
                            <li key={entry._id}>
                                <div className="sf-row flex-wrap sm:flex-nowrap">
                                    <AvatarUser name={clientName} photoURL={undefined} size="h-10 w-10 !text-sm" />
                                    <div className="sf-row-body basis-[calc(100%-3.5rem)] sm:basis-auto">
                                        <span className="sf-row-title">{clientName}</span>
                                        <span className="sf-row-sub">{getWaitlistServiceLabel(entry)}</span>
                                        <span className="sf-row-sub">
                                            {formatDates(entry)} · {minutesToTime(entry?.timeWindowStartMinutes)} às{' '}
                                            {minutesToTime(entry?.timeWindowEndMinutes)} ·{' '}
                                            {entry?.attendant?.name || 'Qualquer atendente'}
                                        </span>
                                        <span className="sf-tags">
                                            <span className="sf-tag" data-tone={WaitlistStatusTones[status] || 'gray'}>
                                                {WaitlistStatusLabels[status] || entry.status}
                                            </span>
                                            {!!entry?.createdAt && (
                                                <span className="sf-row-sub self-center">
                                                    Entrou em {zonedDate(entry.createdAt, entry.timezone).format('DD/MM [às] HH:mm')}
                                                </span>
                                            )}
                                        </span>
                                    </div>

                                    <div className="ml-auto flex shrink-0 items-center gap-1 pl-[3.25rem] sm:pl-0">
                                        {!!phone && (
                                            <a
                                                className="btn btn-ghost btn-sm btn-circle"
                                                href={`https://wa.me/${phone}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`WhatsApp de ${clientName}`}
                                                title="WhatsApp"
                                            >
                                                <MessageCircle aria-hidden />
                                            </a>
                                        )}
                                        {open && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => setAssignEntry(entry)}
                                                >
                                                    Agendar
                                                </button>
                                                <Dropdown
                                                    label={<Ellipsis aria-hidden />}
                                                    ariaLabel={`Mais ações de ${clientName}`}
                                                    unstyled
                                                    buttonClass="btn btn-ghost btn-sm btn-circle"
                                                    actions={[
                                                        {
                                                            label: 'Cancelar entrada',
                                                            action: async () => setPending({ entry, status: WaitlistStatus.CANCELLED }),
                                                        },
                                                        {
                                                            label: 'Marcar como expirada',
                                                            action: async () => setPending({ entry, status: WaitlistStatus.EXPIRED }),
                                                        },
                                                    ]}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {assignEntry && (
                <AssignModal
                    entry={assignEntry}
                    onClose={() => setAssignEntry(null)}
                    onAssigned={async () => {
                        setAssignEntry(null);
                        await reloadData();
                    }}
                />
            )}

            {pending && (
                <ConfirmDialog
                    title={pending.status === WaitlistStatus.CANCELLED ? 'Cancelar entrada?' : 'Marcar como expirada?'}
                    confirmLabel={pending.status === WaitlistStatus.CANCELLED ? 'Cancelar entrada' : 'Marcar como expirada'}
                    cancelLabel="Voltar"
                    loading={updating}
                    onConfirm={handleUpdateStatus}
                    onClose={() => setPending(null)}
                >
                    {pending.entry?.client?.name || 'O cliente'} sai da fila e deixa de receber ofertas de vaga por esta entrada.
                </ConfirmDialog>
            )}
        </div>
    );
}
